import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { 
  FileText, 
  Loader2,
  AlertCircle,
  Upload,
  Download,
  Trash2,
  Filter,
  CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';

const DocumentsPage = () => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState('all');
  
  const [uploadForm, setUploadForm] = useState({
    category: '',
    description: '',
    file: null,
  });

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const response = await api.get('/documents');
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!uploadForm.file || !uploadForm.category) {
      toast.error('Seleccione un archivo y categoría');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('category', uploadForm.category);
      formData.append('description', uploadForm.description);
      formData.append('related_entity_type', 'user');
      formData.append('related_entity_id', user.user_id);
      formData.append('is_confidential', 'true');
      formData.append('access_level', 'admin');

      await api.post('/documents/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('Documento subido exitosamente');
      setUploadForm({ category: '', description: '', file: null });
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al subir documento');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await api.get(`/documents/${doc.document_id}/download`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.original_filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Error al descargar documento');
    }
  };

  const handleDelete = async (docId) => {
    if (!confirm('¿Está seguro de eliminar este documento?')) return;
    
    try {
      await api.delete(`/documents/${docId}`);
      toast.success('Documento eliminado');
      fetchDocuments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar documento');
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      id_document: 'Identificación',
      address_proof: 'Comprobante de Domicilio',
      tax: 'Documentos Fiscales',
      kyc: 'Verificación KYC',
      proof_of_funds: 'Prueba de Fondos',
      proof_of_transfer: 'Comprobante de Transferencia',
      incorporation: 'Acta Constitutiva',
      power_of_attorney: 'Poder Notarial',
      property_title: 'Escrituras',
      property_tax: 'Predial',
      legal: 'Legal',
      financial: 'Financiero',
      contract: 'Contrato',
      other: 'Otro',
    };
    return labels[category] || category;
  };

  const getCategoryBadge = (category) => {
    const colors = {
      id_document: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
      address_proof: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
      tax: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
      kyc: 'bg-purple-500/10 text-purple-500 border-purple-500/20',
      legal: 'bg-red-500/10 text-red-500 border-red-500/20',
      financial: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
    };
    const color = colors[category] || 'bg-muted text-muted-foreground border-muted';
    return <Badge variant="outline" className={color}>{getCategoryLabel(category)}</Badge>;
  };

  const filteredDocuments = filter === 'all' 
    ? documents 
    : documents.filter(d => d.category === filter);

  const uniqueCategories = [...new Set(documents.map(d => d.category))];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" strokeWidth={1.5} />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="documents-page">
      {/* Header */}
      <div>
        <h1 className="heading-2 mb-2">Mis Documentos</h1>
        <p className="body-text">
          Administre sus documentos personales y de verificación.
        </p>
      </div>

      {/* Upload Form */}
      <Card className="card-dashboard">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="h-5 w-5" strokeWidth={1.5} />
            Subir Nuevo Documento
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Categoría *</Label>
                <Select
                  value={uploadForm.category}
                  onValueChange={(v) => setUploadForm(prev => ({ ...prev, category: v }))}
                >
                  <SelectTrigger data-testid="doc-category">
                    <SelectValue placeholder="Seleccione categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="id_document">Identificación Oficial</SelectItem>
                    <SelectItem value="address_proof">Comprobante de Domicilio</SelectItem>
                    <SelectItem value="tax">Documentos Fiscales (RFC, CSF)</SelectItem>
                    <SelectItem value="proof_of_funds">Prueba de Fondos</SelectItem>
                    <SelectItem value="incorporation">Acta Constitutiva (empresas)</SelectItem>
                    <SelectItem value="power_of_attorney">Poder Notarial</SelectItem>
                    <SelectItem value="other">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Descripción (opcional)</Label>
                <Input
                  value={uploadForm.description}
                  onChange={(e) => setUploadForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Ej: INE vigente 2024"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Archivo *</Label>
                <Input
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={(e) => setUploadForm(prev => ({ ...prev, file: e.target.files[0] }))}
                  data-testid="doc-file"
                />
              </div>
            </div>
            
            <div className="flex justify-end">
              <Button type="submit" disabled={uploading} className="btn-primary gap-2">
                {uploading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Upload className="h-4 w-4" strokeWidth={1.5} />
                )}
                Subir Documento
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Filter */}
      {documents.length > 0 && (
        <div className="flex items-center gap-4">
          <Filter className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los documentos</SelectItem>
              {uniqueCategories.map(cat => (
                <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="text-center py-16">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" strokeWidth={1.5} />
            <h3 className="text-lg font-medium mb-2">No tiene documentos</h3>
            <p className="text-muted-foreground">
              Suba sus documentos para completar su verificación.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredDocuments.map((doc) => (
            <Card key={doc.document_id} className="card-dashboard hover:border-primary/30 transition-colors">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-muted rounded-sm">
                      <FileText className="h-5 w-5 text-muted-foreground" strokeWidth={1.5} />
                    </div>
                    <div>
                      <p className="font-medium">{doc.description || doc.original_filename}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {getCategoryBadge(doc.category)}
                        <span className="text-xs text-muted-foreground">
                          {new Date(doc.created_at).toLocaleDateString('es-MX')}
                        </span>
                        <span className="text-xs text-muted-foreground">
                          {(doc.size / 1024).toFixed(1)} KB
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDownload(doc)}
                      className="gap-2"
                    >
                      <Download className="h-4 w-4" strokeWidth={1.5} />
                      Descargar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(doc.document_id)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info */}
      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Sus documentos son confidenciales y solo son accesibles por usted y el equipo de cumplimiento de Silver Brick. Aceptamos archivos PDF, JPG y PNG de hasta 10MB.
        </AlertDescription>
      </Alert>
    </div>
  );
};

export default DocumentsPage;
